import PolicyShell, { Section, SubHead, Bullets } from "@/components/PolicyShell";

export default function ShippingPage() {
  return (
    <PolicyShell
      testId="shipping-page"
      overline="BOSCAGE Elegance"
      title="Shipping Policy"
      contact="hello@boscage.com  ·  www.boscage.com"
      lastUpdated="Last updated: June 2026  |  BOSCAGE Elegance"
    >
      <p>
        At BOSCAGE Elegance we ship exclusively across North America — the United States, Canada, and Mexico —
        using insured, traceable couriers. This policy explains our processing times, delivery windows, costs, and
        what to do if something goes wrong in transit.
      </p>

      <Section title="1. Order Processing">
        <p>
          Orders are processed Monday through Friday (excluding US public holidays). Most in-stock orders are quality
          checked, packed and dispatched from our facility within <strong className="text-[#000000]">1–2 business days</strong>.
        </p>
        <Bullets
          items={[
            "Orders placed before 12:00 PM EST on a business day are typically dispatched the same or next business day.",
            "Orders placed on weekends or US public holidays are processed on the next business day.",
            "Personalized, bespoke or pre-order pieces ship 14 business days from the date of order confirmation.",
          ]}
        />
      </Section>

      <Section title="2. Delivery Methods & Estimated Timelines">
        <p>
          Once dispatched, you will receive an email with a tracking number to monitor your shipment in real time.
          Estimated transit windows below are after dispatch and exclude order processing time.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="shipping-rates-table">
            <thead className="text-[#c9a84c]">
              <tr className="border-b border-[#000000]/15">
                <th className="py-3 pr-4 font-medium uppercase tracking-[0.16em] text-[11px]">Destination</th>
                <th className="py-3 pr-4 font-medium uppercase tracking-[0.16em] text-[11px]">Service</th>
                <th className="py-3 pr-4 font-medium uppercase tracking-[0.16em] text-[11px]">Transit Time</th>
                <th className="py-3 font-medium uppercase tracking-[0.16em] text-[11px]">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#000000]/10">
              <tr><td className="py-3 pr-4">United States</td><td className="py-3 pr-4">Standard</td><td className="py-3 pr-4">7 business days</td><td className="py-3">Complimentary</td></tr>
              <tr><td className="py-3 pr-4">United States</td><td className="py-3 pr-4">Expedite</td><td className="py-3 pr-4">3–4 business days</td><td className="py-3">Complimentary</td></tr>
              <tr><td className="py-3 pr-4">United States</td><td className="py-3 pr-4">Pre-Order</td><td className="py-3 pr-4">14 business days</td><td className="py-3">Complimentary</td></tr>
              <tr><td className="py-3 pr-4">Canada</td><td className="py-3 pr-4">Standard</td><td className="py-3 pr-4">7 business days</td><td className="py-3">Complimentary</td></tr>
              <tr><td className="py-3 pr-4">Canada</td><td className="py-3 pr-4">Expedite</td><td className="py-3 pr-4">3–4 business days</td><td className="py-3">Complimentary</td></tr>
              <tr><td className="py-3 pr-4">Canada</td><td className="py-3 pr-4">Pre-Order</td><td className="py-3 pr-4">14 business days</td><td className="py-3">Complimentary</td></tr>
              <tr><td className="py-3 pr-4">Mexico</td><td className="py-3 pr-4">Standard</td><td className="py-3 pr-4">7 business days</td><td className="py-3">Complimentary</td></tr>
              <tr><td className="py-3 pr-4">Mexico</td><td className="py-3 pr-4">Expedite</td><td className="py-3 pr-4">3–4 business days</td><td className="py-3">Complimentary</td></tr>
              <tr><td className="py-3 pr-4">Mexico</td><td className="py-3 pr-4">Pre-Order</td><td className="py-3 pr-4">14 business days</td><td className="py-3">Complimentary</td></tr>
            </tbody>
          </table>
        </div>

        <SubHead>Important Notes</SubHead>
        <Bullets
          items={[
            "We only ship to North America (USA, Canada, Mexico).",
            "All shipping is complimentary (free) for all orders.",
            "Standard delivery: 7 business days.",
            "Expedite delivery: 3–4 business days (in-stock items only).",
            "Pre-Order items: 14 business days (made-to-order).",
            "Transit windows are estimates provided by our carriers and may vary slightly due to weather, customs inspections, or regional carrier capacity.",
          ]}
        />
      </Section>

      <Section title="3. Shipping Costs">
        <p>
          BOSCAGE offers <strong className="text-[#000000]">complimentary shipping</strong> on all orders across the
          United States, Canada, and Mexico. The price you see at checkout is the price you pay — no separate
          shipping fees are added.
        </p>
      </Section>

      <Section title="4. Customs, Duties & Import Taxes">
        <p>
          For US domestic orders, applicable sales tax is included at checkout. For orders shipped to Canada or Mexico,
          your shipment may be subject to import duties, taxes, or customs clearance fees imposed by the destination country.
        </p>
        <Bullets
          items={[
            "These charges are determined by the destination country and are the responsibility of the recipient.",
            "BOSCAGE has no control over the amount of duties or taxes assessed by local customs authorities.",
            "If a parcel is refused by the recipient and returned to us due to unpaid duties, any refund will exclude original outbound shipping costs and any return shipping fees incurred.",
          ]}
        />
        <p>
          Please contact your local customs office before ordering if you would like an estimate of any charges that
          may apply.
        </p>
      </Section>

      <Section title="5. Tracking Your Order">
        <p>
          As soon as your order is dispatched, you will receive a shipping confirmation email with a tracking number
          and a link to your carrier&apos;s tracking page. You can also view tracking from your account dashboard at
          any time.
        </p>
        <p>
          If your tracking has not updated for 5 or more business days, please contact hello@boscage.com so we can
          open an investigation with the carrier on your behalf.
        </p>
      </Section>

      <Section title="6. Delivery, Signatures & Address Accuracy">
        <p>
          For the security of high-value items, all BOSCAGE shipments require a signature upon delivery. If no one is
          available to sign, the carrier will leave a notice and reattempt delivery or hold the parcel at a local
          collection point.
        </p>
        <p>
          It is the customer&apos;s responsibility to provide an accurate, complete shipping address at checkout.
          BOSCAGE is not liable for delays or non-delivery caused by an incorrect or incomplete address. Address
          changes after dispatch are subject to carrier approval and may incur a fee.
        </p>
      </Section>

      <Section title="7. Lost, Delayed or Damaged Shipments">
        <SubHead>Delayed</SubHead>
        <p>
          If your shipment is significantly delayed beyond the estimated transit window, contact hello@boscage.com.
          We will work with the carrier to investigate and update you as soon as possible.
        </p>
        <SubHead>Lost in Transit</SubHead>
        <p>
          If a shipment is officially declared lost by the carrier, BOSCAGE will arrange a replacement or full refund
          at no additional cost to you.
        </p>
        <SubHead>Damaged on Arrival</SubHead>
        <p>
          Inspect your parcel upon delivery. If the packaging is visibly damaged or the contents are compromised,
          email hello@boscage.com within 48 hours of receipt with clear photos of both the outer box and the affected
          product. We will arrange a replacement or refund in accordance with our Returns &amp; Exchanges Policy.
        </p>
      </Section>

      <Section title="8. Pre-Orders, Bespoke & Personalized Pieces">
        <p>
          Bespoke and personalized jackets created through our Personalize programme are made to order and ship
          14 business days after order confirmation. You will receive a dedicated production update at the halfway
          point and a tracking email on dispatch.
        </p>
      </Section>

      <Section title="9. PO Boxes & Restricted Destinations">
        <p>
          For security reasons, BOSCAGE does not ship to PO Boxes, freight-forwarding addresses or APO/FPO addresses.
          A residential or business address with a recipient available to sign is required.
        </p>
        <p>
          BOSCAGE currently ships exclusively to the United States, Canada, and Mexico. Orders placed with a
          delivery address outside North America cannot be fulfilled and will be refunded in full.
        </p>
      </Section>

      <Section title="10. Contact">
        <p>
          For any shipping question or to expedite an order, please reach our team:
        </p>
        <p>
          Email: hello@boscage.com
          <br />
          Address: 5856 Mount Vernon Dr, VA, Alexandria, VA 22303, United States
        </p>
      </Section>
    </PolicyShell>
  );
}
